import { useToast } from '@chakra-ui/react'
import { faExclamationTriangle } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import React, { useEffect, useState } from 'react'
import { useUpdateWalletMutation } from '../redux/services/coreApi'
import type { WalletDto, WalletHostingType } from '../types/coreApi-types'
import DefaultModal from './DefaultModal'

interface UpdateWalletHostingTypeModalProps {
  isOpen: boolean
  onClose: () => void
  wallet: WalletDto | null
}

const UpdateWalletHostingTypeModal: React.FC<
  UpdateWalletHostingTypeModalProps
> = ({ isOpen, onClose, wallet }) => {
  const toast = useToast()
  const [hostingType, setHostingType] = useState<WalletHostingType>('Unknown')
  const [hostedVaspId, setHostedVaspId] = useState<string>('')

  const [updateWallet, { isLoading }] = useUpdateWalletMutation()

  useEffect(() => {
    if (wallet) {
      setHostingType(wallet.hostingType || 'Unknown')
      setHostedVaspId(wallet.hostedVaspId || '')
    }
  }, [wallet])

  const handleSubmit = async () => {
    if (!wallet) return

    try {
      await updateWallet({
        id: wallet.id,
        hostingType,
        hostedVaspId: hostedVaspId || null,
      }).unwrap()

      toast({
        title: 'Success',
        description: 'Wallet hosting type updated successfully',
        status: 'success',
        duration: 5000,
        isClosable: true,
      })

      onClose()
    } catch (err) {
      toast({
        title: 'Error',
        description: 'Failed to update wallet hosting type',
        status: 'error',
        duration: 5000,
        isClosable: true,
      })
    }
  }

  const getDisplayText = (type: WalletHostingType) => {
    if (type === 'SelfHosted') return 'Self hosted'
    if (type === 'VaspHosted') return 'Exchange hosted'
    return 'Unknown'
  }

  const getTooltipText = (type: WalletHostingType) => {
    if (type === 'SelfHosted') {
      return 'Your wallet is stored locally on your device using apps like MetaMask, Trust Wallet, or other self-custody solutions.'
    }
    if (type === 'VaspHosted') {
      return 'Your wallet is hosted by a cryptocurrency exchange (like Coinbase, Binance, Kraken, etc.) or another third-party service that holds your funds.'
    }
    return 'Unknown wallet hosting type.'
  }

  return (
    <DefaultModal
      isOpen={isOpen}
      onClose={onClose}
      title="Update Wallet Hosting Type"
      isLoading={isLoading}
      isLoadingText="Updating wallet hosting type..."
      maxW="md"
    >
      <div className="p-5 space-y-6">
        {wallet && (
          <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg">
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">
              Wallet Address
            </p>
            <p className="font-mono text-sm break-all">{wallet.address}</p>
            {wallet.name && (
              <>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-1 mt-2">
                  Wallet Name
                </p>
                <p className="text-sm">{wallet.name}</p>
              </>
            )}
          </div>
        )}

        <div>
          <p className="mb-3 text-description">
            Wallet Hosting Type <span className="text-red-500">*</span>
          </p>
          <div className="space-y-3">
            {(['SelfHosted', 'VaspHosted'] as WalletHostingType[]).map(type => (
              <label
                key={type}
                className="flex items-start gap-3 cursor-pointer p-3 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
              >
                <input
                  type="radio"
                  name="hostingType"
                  value={type}
                  checked={hostingType === type}
                  onChange={e =>
                    setHostingType(e.target.value as WalletHostingType)
                  }
                  className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 focus:ring-blue-500 mt-0.5"
                />
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium">
                      {getDisplayText(type)}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    {getTooltipText(type)}
                  </p>
                </div>
              </label>
            ))}
          </div>
        </div>

        {hostingType === 'VaspHosted' && (
          <div>
            <p className="mb-2 text-description">
              Exchange/Service Name{' '}
              <span className="text-gray-400">(Optional)</span>
            </p>
            <input
              type="text"
              value={hostedVaspId}
              onChange={e => setHostedVaspId(e.target.value)}
              placeholder="e.g., Coinbase, Binance, Kraken"
              className="input-card w-full"
            />
          </div>
        )}

        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
          <div className="flex items-start gap-2">
            <FontAwesomeIcon
              icon={faExclamationTriangle}
              className="w-4 h-4 text-yellow-600 dark:text-yellow-400 mt-0.5 flex-shrink-0"
            />
            <div>
              <p className="text-sm font-medium text-yellow-800 dark:text-yellow-200">
                Important Notice
              </p>
              <p className="text-xs text-yellow-700 dark:text-yellow-300 mt-1">
                You must declare your wallet hosting type to use it for
                cryptocurrency transfers. This information is required for
                compliance and security purposes.
              </p>
            </div>
          </div>
        </div>

        <div className="flex gap-3 pt-4">
          <button
            type="button"
            onClick={onClose}
            className="button-secondary flex-1"
            disabled={isLoading}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            className="button flex-1"
            disabled={isLoading}
          >
            {isLoading ? 'Updating...' : 'Update'}
          </button>
        </div>
      </div>
    </DefaultModal>
  )
}

export default UpdateWalletHostingTypeModal
